import { DuplicateConflict } from '../../features/domain-bulk-import/services/BulkImportService';

/**
 * Service for generating user-friendly skip reasons for domain import conflicts
 */
export class SkipReasonService {
    /**
     * Generate a clear, user-friendly reason for why a domain is being skipped
     * @param conflict The duplicate conflict information
     * @returns A descriptive skip reason string
     */
    static generateSkipReason(conflict: DuplicateConflict): string {
        const { existsInSystem, existsInFile, domainName, normalizedName } = conflict;

        // Handle combined scenarios first (most specific)
        if (existsInSystem && existsInFile) {
            return `Domain "${domainName}" already exists in your account and appears multiple times in the uploaded file`;
        }

        // Handle system duplicates
        if (existsInSystem) {
            // Show normalization if the original differs from normalized
            if (domainName !== normalizedName) {
                return `Domain "${domainName}" (normalized as "${normalizedName}") already exists in your account`;
            }
            return `Domain "${domainName}" already exists in your account`;
        }

        // Handle file duplicates
        if (existsInFile) {
            // Show normalization if the original differs from normalized
            if (domainName !== normalizedName) {
                return `Duplicate domain "${domainName}" (normalized as "${normalizedName}") found in uploaded file - keeping first occurrence only`;
            }
            return `Duplicate domain "${domainName}" found in uploaded file - keeping first occurrence only`;
        }

        // Fallback for unknown scenarios
        return `Domain "${domainName}" cannot be imported due to duplicate conflict`;
    }

    /**
     * Generate a skip reason for domains that fail the final safety check
     * @param domainName The original domain name
     * @param normalizedName The normalized domain name
     * @param reason The specific reason from the safety check
     * @returns A descriptive skip reason string
     */
    static generateSafetyCheckSkipReason(
        domainName: string, 
        normalizedName: string, 
        reason: string
    ): string {
        // Show normalization if the original differs from normalized
        if (domainName !== normalizedName) {
            return `Domain "${domainName}" (normalized as "${normalizedName}") skipped: ${reason}`;
        }
        return `Domain "${domainName}" skipped: ${reason}`;
    }

    /**
     * Generate a category-based skip reason for reporting purposes
     * @param conflict The duplicate conflict information
     * @returns An object with category and reason
     */
    static generateCategorizedSkipReason(conflict: DuplicateConflict): {
        category: 'system_duplicate' | 'file_duplicate' | 'combined_duplicate' | 'unknown';
        reason: string;
    } {
        const { existsInSystem, existsInFile } = conflict;

        if (existsInSystem && existsInFile) {
            return {
                category: 'combined_duplicate',
                reason: this.generateSkipReason(conflict)
            };
        }

        if (existsInSystem) {
            return {
                category: 'system_duplicate',
                reason: this.generateSkipReason(conflict)
            };
        }

        if (existsInFile) {
            return {
                category: 'file_duplicate',
                reason: this.generateSkipReason(conflict)
            };
        }

        return {
            category: 'unknown',
            reason: this.generateSkipReason(conflict)
        };
    }

    /**
     * Generate a short, actionable skip reason for UI display
     * @param conflict The duplicate conflict information
     * @returns A concise skip reason suitable for tables or lists
     */
    static generateShortSkipReason(conflict: DuplicateConflict): string {
        const { existsInSystem, existsInFile } = conflict;

        if (existsInSystem && existsInFile) {
            return 'Exists in account + duplicate in file';
        }

        if (existsInSystem) {
            return 'Already exists in account';
        }

        if (existsInFile) {
            return 'Duplicate in file';
        }

        return 'Duplicate conflict';
    }
}